import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ValidationTable = ({ data, onPlayAudio, onApprove, onReject }) => {
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    if (status === 'pass') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <Icon name="CheckCircle" size={14} />
          Pass
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <Icon name="XCircle" size={14} />
        Fail
      </span>
    );
  };

  const getQualityScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text || text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + '...';
  };

  if (!data || data?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="FileAudio" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Audio Files Found</h3>
        <p className="text-muted-foreground">No audio recordings match your current filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Line ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Play / Character
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Dialogue
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Quality Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {data?.map((item) => (
              <tr key={item?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Icon name="FileAudio" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">{item?.lineId}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div className="font-medium text-foreground">{item?.playTitle}</div>
                    <div className="text-muted-foreground">{item?.character}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-foreground max-w-xs">
                    {truncateText(item?.dialogue)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={14} className="text-muted-foreground" />
                    <span className={`text-sm font-medium ${item?.durationValid ? 'text-foreground' : 'text-red-600'}`}>
                      {formatDuration(item?.duration)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${item?.qualityScore >= 70 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${item?.qualityScore}%` }}
                      />
                    </div>
                    <span className={`text-sm font-semibold ${getQualityScoreColor(item?.qualityScore)}`}>
                      {item?.qualityScore}%
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(item?.validationStatus)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onPlayAudio(item)}
                      className="p-2 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                      title="Play Audio"
                    >
                      <Icon name="Play" size={16} />
                    </button>
                    <button
                      onClick={() => onApprove(item?.id)}
                      className="p-2 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                      title="Approve"
                    >
                      <Icon name="Check" size={16} />
                    </button>
                    <button
                      onClick={() => onReject(item?.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-600 transition-colors"
                      title="Reject"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {data?.map((item) => (
          <div key={item?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="FileAudio" size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-foreground">{item?.lineId}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {item?.playTitle} • {item?.character}
                </div>
              </div>
              {getStatusBadge(item?.validationStatus)}
            </div>

            <p className="text-sm text-foreground mb-3">{truncateText(item?.dialogue, 80)}</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Duration</div>
                <div className={`text-sm font-medium ${item?.durationValid ? 'text-foreground' : 'text-red-600'}`}>
                  {formatDuration(item?.duration)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Quality Score</div>
                <div className={`text-sm font-semibold ${getQualityScoreColor(item?.qualityScore)}`}>
                  {item?.qualityScore}%
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => onPlayAudio(item)}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <Icon name="Play" size={14} className="mr-1" />
                Play
              </Button>
              <button
                onClick={() => onApprove(item?.id)}
                className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
              >
                <Icon name="Check" size={16} />
              </button>
              <button
                onClick={() => onReject(item?.id)}
                className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ValidationTable;
